"use client";
import { LAPYME_BANK_TRANSFER_DETAILS } from "@workspace/shared/billing-payment-details";
import { formatAR } from "@workspace/shared/date";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@workspace/ui/components/alert-dialog";
import { Button, buttonVariants } from "@workspace/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@workspace/ui/components/dialog";
import { toast } from "@workspace/ui/components/sonner";
import { CircleAlert } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import {
  CopyButton,
  CopyButtonIcon,
  CopyButtonTrigger,
} from "@/features/common/ui/copy-button";
import { notifyPaymentAction } from "../payment-notification.actions";
import type { SubscriptionInvoice } from "../subscription-invoices";
import { SubscriptionInvoicesList } from "./subscription-invoices-list";

type PaymentNotificationBannerProps = {
  orgSlug: string;
  subscriptionStatus: string | null;
  paymentMethod: string | null;
  billingInvoicePaymentStatus:
    | "awaiting_confirmation"
    | "unpaid"
    | "partially_paid"
    | "paid"
    | "failed"
    | "refunded"
    | null;
  hasNotifiedCurrentPeriod: boolean;
  billingPeriodStart: string | null;
  canManagePayment: boolean;
  subscriptionInvoices?: SubscriptionInvoice[];
  referenceTime: string;
};

function getDaysOverdue(
  billingPeriodStart: string | null,
  referenceTime: string
): number {
  if (!billingPeriodStart) {
    return 0;
  }
  const start = new Date(`${billingPeriodStart}T00:00:00-03:00`);
  const now = new Date(referenceTime);
  return Math.max(
    0,
    Math.floor((now.getTime() - start.getTime()) / 86_400_000)
  );
}

function formatDaysOverdue(daysOverdue: number): string {
  if (daysOverdue === 1) {
    return "ayer";
  }
  return `${daysOverdue} días`;
}

export function PaymentNotificationBanner({
  orgSlug,
  subscriptionStatus,
  paymentMethod,
  billingInvoicePaymentStatus,
  hasNotifiedCurrentPeriod,
  billingPeriodStart,
  canManagePayment,
  subscriptionInvoices = [],
  referenceTime,
}: PaymentNotificationBannerProps) {
  const router = useRouter();
  const [hasConfirmedPayment, setHasConfirmedPayment] = useState(false);
  const [isPending, startTransition] = useTransition();

  if (
    (paymentMethod === "rebill" || paymentMethod === "mercadopago") &&
    (subscriptionStatus === "paused" ||
      subscriptionStatus === "past_due" ||
      subscriptionStatus === "incomplete" ||
      subscriptionStatus === "canceled")
  ) {
    return (
      <div className="flex items-center justify-center gap-3 bg-destructive/10 px-4 py-2 text-destructive-foreground text-sm">
        <CircleAlert className="size-4 shrink-0" />
        <span>
          No pudimos procesar tu último pago. Para mantener la suscripción,
          reintentá el pago.
        </span>
        <a
          className={buttonVariants({ size: "sm" })}
          href={`/suscribirse/${orgSlug}`}
        >
          Ir a pagar
        </a>
      </div>
    );
  }

  if (paymentMethod !== "bank_transfer") {
    return null;
  }

  if (subscriptionStatus !== "active" && subscriptionStatus !== "past_due") {
    return null;
  }

  if (hasNotifiedCurrentPeriod || hasConfirmedPayment) {
    return null;
  }

  function handleConfirm() {
    startTransition(async () => {
      const result = await notifyPaymentAction(orgSlug);
      if (result.success) {
        setHasConfirmedPayment(true);
        router.refresh();
        toast.success("Pago informado correctamente");
      } else {
        toast.error(result.error);
      }
    });
  }

  const daysOverdue = getDaysOverdue(billingPeriodStart, referenceTime);
  const formattedDate = billingPeriodStart
    ? formatAR(`${billingPeriodStart}T00:00:00-03:00`, "d 'de' MMMM")
    : "";

  const isPastDue = subscriptionStatus === "past_due";

  function getMessage() {
    if (isPastDue && billingInvoicePaymentStatus === "failed") {
      return "Tu último pago fue rechazado. Volvé a informar la transferencia.";
    }
    if (daysOverdue === 0) {
      return "Hoy vence tu suscripción. Si ya transferiste, informalo acá.";
    }
    return `Tu pago venció el ${formattedDate} (${formatDaysOverdue(daysOverdue)}). Si ya transferiste, informalo acá.`;
  }

  const message = getMessage();

  return (
    <div className="flex items-center justify-center gap-3 bg-destructive/10 px-4 py-2 text-destructive-foreground text-sm">
      <CircleAlert className="size-4 shrink-0" />
      <span>{message}</span>
      {canManagePayment && (
        <>
          <Dialog>
            <DialogTrigger render={<Button size="sm" variant="outline" />}>
              Ver facturas
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Facturas pendientes</DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                {subscriptionInvoices.length > 0 ? (
                  <SubscriptionInvoicesList
                    invoices={subscriptionInvoices}
                    orgSlug={orgSlug}
                  />
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No encontramos facturas pendientes para mostrar.
                  </p>
                )}

                <div className="space-y-3 border-t pt-4 text-sm">
                  <p className="font-medium">Datos para transferencia</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-muted-foreground">Alias</p>
                      <p className="font-medium">
                        {LAPYME_BANK_TRANSFER_DETAILS.alias}
                      </p>
                    </div>
                    <CopyButton value={LAPYME_BANK_TRANSFER_DETAILS.alias}>
                      <CopyButtonTrigger
                        aria-label="Copiar alias"
                        className="text-muted-foreground"
                        size="icon-sm"
                        variant="ghost"
                      >
                        <CopyButtonIcon className="size-3.5" />
                      </CopyButtonTrigger>
                    </CopyButton>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-muted-foreground">CVU</p>
                      <p className="font-medium font-mono">
                        {LAPYME_BANK_TRANSFER_DETAILS.cvu}
                      </p>
                    </div>
                    <CopyButton value={LAPYME_BANK_TRANSFER_DETAILS.cvu}>
                      <CopyButtonTrigger
                        aria-label="Copiar CVU"
                        className="text-muted-foreground"
                        size="icon-sm"
                        variant="ghost"
                      >
                        <CopyButtonIcon className="size-3.5" />
                      </CopyButtonTrigger>
                    </CopyButton>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Titular</p>
                    <p className="font-medium">
                      {LAPYME_BANK_TRANSFER_DETAILS.accountHolder}
                    </p>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <AlertDialog>
            <AlertDialogTrigger render={<Button size="sm" />}>
              Ya transferí
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar pago</AlertDialogTitle>
                <AlertDialogDescription>
                  Estás confirmando que ya abonaste la suscripción del mes. Esto
                  va a notificar a La Pyme para confirmar la acreditación del
                  pago manualmente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction disabled={isPending} onClick={handleConfirm}>
                  Informar pago
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
